import { PaymentAssignmentStatusId, type PaymentStatus } from './types'

/** Lower `order` = earlier in filters, dropdowns, and metrics columns. */
export interface PaymentAssignmentStatusOrderEntry {
  order: number
}

/**
 * Single source of truth for payment-assignment status display order.
 * Membership for roles/metrics stays in those modules; this only defines sequence.
 */
export const PAYMENT_ASSIGNMENT_STATUS_ORDER = {
  [PaymentAssignmentStatusId.PENDING_ASSIGNMENT]: { order: 0 },
  [PaymentAssignmentStatusId.PENDING_PAYMENT]: { order: 1 },
  [PaymentAssignmentStatusId.PAYMENT_SUBMITTED]: { order: 2 },
  [PaymentAssignmentStatusId.PAYMENT_CLEARED]: { order: 3 },
  [PaymentAssignmentStatusId.PENDING_AUTOPAY]: { order: 4 },
  [PaymentAssignmentStatusId.AP_CONFIRMED]: { order: 5 },
  [PaymentAssignmentStatusId.PAID_ESCALATION]: { order: 6 },
  [PaymentAssignmentStatusId.UNPAID_ESCALATION]: { order: 7 },
  [PaymentAssignmentStatusId.ESCALATION_FULLY_RESOLVED]: { order: 8 },
  [PaymentAssignmentStatusId.TERMINATED_EE]: { order: 9 },
  [PaymentAssignmentStatusId.TERMINATED_PLAN]: { order: 10 },
  [PaymentAssignmentStatusId.EE_PAID]: { order: 11 },
  [PaymentAssignmentStatusId.NEW_EFFECTIVE_DATE]: { order: 12 },
  [PaymentAssignmentStatusId.CARRIER_SHOW_PAYMENT]: { order: 13 },
  [PaymentAssignmentStatusId.REATTEMPT]: { order: 14 },
  [PaymentAssignmentStatusId.BOT_QUEUED]: { order: 15 },
  [PaymentAssignmentStatusId.BOT_PROCESSING]: { order: 16 },
  [PaymentAssignmentStatusId.BOT_SUBMITTED]: { order: 17 },
  [PaymentAssignmentStatusId.BOT_CLEARED]: { order: 18 },
  [PaymentAssignmentStatusId.BOT_FAILED]: { order: 19 },
  [PaymentAssignmentStatusId.BOT_CANCELLED]: { order: 20 },
} as const satisfies Record<
  Exclude<
    PaymentAssignmentStatusId,
    PaymentAssignmentStatusId.PENDING_RESOLVE_ISSUE
  >,
  PaymentAssignmentStatusOrderEntry
>

function getPaymentAssignmentStatusSortIndex(
  id: PaymentAssignmentStatusId | string | null | undefined
): number {
  if (!id) return Number.MAX_SAFE_INTEGER
  const entry = PAYMENT_ASSIGNMENT_STATUS_ORDER[id as PaymentAssignmentStatusId]
  return entry?.order ?? Number.MAX_SAFE_INTEGER
}

function comparePaymentAssignmentStatusesByDisplayOrder(
  a: PaymentAssignmentStatusId,
  b: PaymentAssignmentStatusId
): number {
  return (
    getPaymentAssignmentStatusSortIndex(a) -
    getPaymentAssignmentStatusSortIndex(b)
  )
}

/** Sorts by {@link PAYMENT_ASSIGNMENT_STATUS_ORDER} (each status has a unique `order`). */
export function sortPaymentAssignmentStatusesByDisplayOrder<
  T extends PaymentStatus,
>(statuses: readonly T[]): T[] {
  return [...statuses].sort((x, y) =>
    comparePaymentAssignmentStatusesByDisplayOrder(
      x as PaymentAssignmentStatusId,
      y as PaymentAssignmentStatusId
    )
  )
}
